import { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import {
  Cpu,
  LineChart,
  BatteryCharging,
  Building,
  Building2,
  Globe2,
  Activity,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

// DICIONÁRIO DE TRADUÇÕES (Centralizado e reativo)
const TRANSLATIONS = {
  en: {
    heroEyebrow: "About WSBP",
    heroSubtitle: "An engineering company dedicated to automation and software solutions for energy systems and technical infrastructure.",
    whoEyebrow: "Who we are",
    whoTitle: "From the University of Coimbra to 1 GW under control.",
    whoDesc1: "Founded in 2008 as a spin-off of the Department of Mechanical Engineering of the University of Coimbra, WSBP started out focused on energy efficiency and centralised building management (BMS).",
    whoDesc2: "With almost two decades of experience, we have evolved from an academic foundation to become a reference technology partner — combining the development of in-house monitoring platforms with advanced control solutions for the renewable energy sector.",
    softwareEyebrow: "Proprietary software",
    softwareTitle: "A robust tool for long-term decisions.",
    softwareList: [
      {
        name: "JANUS",
        icon: Activity,
        desc: "Web platform for real-time monitoring and analysis, focused on energy efficiency and sustainability. Tracks distributed networks of facilities, integrates environmental and energy sensor data and produces automated reporting.",
      },
    ],
    pillarsEyebrow: "What we do",
    pillarsTitle: "Six pillars. One engineering platform.",
    pillarsList: [
      { icon: Building2, title: "Building management", desc: "Centralised technical management (BMS) for airports, hospitals, hotels, schools and municipal facilities." },
      { icon: Cpu, title: "Proprietary software", desc: "JANUS as the foundation for real-time energy monitoring." },
      { icon: LineChart, title: "PPC & SCADA", desc: "Real-time coordination and full supervision of energy assets." },
      { icon: BatteryCharging, title: "BESS & hybrids", desc: "Storage integration, SoC management and power flow control." },
      { icon: Building, title: "Substations", desc: "Automation and SCADA solutions for electrical substations." },
      { icon: Globe2, title: "International", desc: "Projects in Portugal, Romania and across European markets." },
    ],
    timelineEyebrow: "Journey",
    timelineTitle: "Milestones.",
    timelineList: [
      {
        year: "2008",
        title: "Founded",
        desc: "Spin-off of the Department of Mechanical Engineering of the University of Coimbra. Initial focus on energy efficiency and BMS.",
      },
      {
        year: "2010+",
        title: "JANUS",
        desc: "Launch of the proprietary platform for real-time energy monitoring.",
      },
      {
        year: "2019",
        title: "Entry into renewables",
        desc: "Specialisation in PPC and SCADA for PV plants, hybrid sites, BESS and substations.",
      },
      {
        year: "Today",
        title: "1 GW+ integrated",
        desc: "Over 1 GW of integrated PV capacity, in domestic and international projects.",
      },
    ]
  },
  pt: {
    heroEyebrow: "Sobre a WSBP",
    heroSubtitle: "Uma empresa de engenharia dedicada ao desenvolvimento de soluções de automação e software para sistemas de energia e infraestruturas técnicas.",
    whoEyebrow: "Quem somos",
    whoTitle: "Da Universidade de Coimbra a mais de 1 GW sob controlo.",
    whoDesc1: "Fundada em 2008 como uma spin-off do Departamento de Engenharia Mecânica da Universidade de Coimbra, a WSBP iniciou a sua atividade focada na eficiência energética e na gestão técnica centralizada (GTC/BMS).",
    whoDesc2: "Com quase duas décadas de experiência, evoluímos de uma base académica para nos tornarmos um parceiro tecnológico de referência — combinando o desenvolvimento de plataformas próprias de monitorização com soluções avançadas de controlo para o setor das energias renováveis.",
    softwareEyebrow: "Software proprietário",
    softwareTitle: "Uma ferramenta robusta para decisões de longo prazo.",
    softwareList: [
      {
        name: "JANUS",
        icon: Activity,
        desc: "Plataforma web de monitorização e análise em tempo real, focada em eficiência energética e sustentabilidade. Acompanha redes distribuídas de instalações, integra dados de sensores ambientais e de energia e gera relatórios automáticos.",
      },
    ],
    pillarsEyebrow: "O que fazemos",
    pillarsTitle: "Seis pilares. Uma plataforma de engenharia.",
    pillarsList: [
      { icon: Building2, title: "Gestão Técnica (BMS)", desc: "Gestão técnica centralizada de edifícios — aeroportos, hospitais, hotéis, escolas e instalações municipais." },
      { icon: Cpu, title: "Software proprietário", desc: "Plataforma JANUS como base fundacional para a monitorização de energia em tempo real." },
      { icon: LineChart, title: "PPC e SCADA", desc: "Coordenação em tempo real e supervisão total de ativos energéticos de grande escala." },
      { icon: BatteryCharging, title: "BESS e Híbridos", desc: "Integração de armazenamento, gestão de Estado de Carga (SoC) e controlo de fluxo de potência." },
      { icon: Building, title: "Subestações", desc: "Soluções completas de automação e sistemas SCADA para subestações elétricas." },
      { icon: Globe2, title: "Internacional", desc: "Projetos consolidados em Portugal, Roménia e nos principais mercados europeus." },
    ],
    timelineEyebrow: "Percurso",
    timelineTitle: "Marcos históricos.",
    timelineList: [
      {
        year: "2008",
        title: "Fundação",
        desc: "Spin-off do Departamento de Engenharia Mecânica da Universidade de Coimbra. Foco inicial em eficiência energética e BMS.",
      },
      {
        year: "2010+",
        title: "JANUS",
        desc: "Lançamento da plataforma proprietária para monitorização de energia em tempo real.",
      },
      {
        year: "2019",
        title: "Entrada nos Renováveis",
        desc: "Especialização em PPC e SCADA para centrais fotovoltaicas, ativos híbridos, BESS e subestações.",
      },
      {
        year: "Hoje",
        title: "1 GW+ Integrado",
        desc: "Mais de 1 GW de capacidade fotovoltaica integrada em projetos nacionais e internacionais.",
      },
    ]
  }
};

export default function About() {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow={<div className="min-w-[120px]">{t.heroEyebrow}</div>}
        title={
          <div className="min-h-[1.2em]">
            {language === "pt" ? (
              <>Engenharia que <span className="text-[#F68A31]">resolve</span> problemas.</>
            ) : (
              <>Engineering that <span className="text-[#F68A31]">solves</span> problems.</>
            )}
          </div>
        }
        subtitle={
          <div className="block min-h-[4rem] sm:min-h-[3rem] md:min-h-[2.5rem] lg:min-h-[2rem]">
            {t.heroSubtitle}
          </div>
        }
        image="https://customer-assets.emergentagent.com/job_solar-scada-monitor/artifacts/q0jcs6xp_PV%20Plant%202.jpg"
      />

      {/* INTRO / HISTORY */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-10 md:px-12 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="eyebrow">{t.whoEyebrow}</div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                {t.whoTitle}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-2 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-lg text-white/75">
                {t.whoDesc1}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-lg text-white/75">
                {t.whoDesc2}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROPRIETARY SOFTWARE */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-16" data-testid="proprietary-software">
        <Reveal>
          <div className="eyebrow">{t.softwareEyebrow}</div>
          <h2 className="font-display mt-6 max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl">
            {t.softwareTitle}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6">
          {t.softwareList.map((s) => {
            const IconComponent = s.icon;
            return (
              <Reveal key={s.name} delay={0.05}>
                <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-colors hover:border-[#F68A31]/50">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F68A31]/10 text-[#F68A31]">
                      <IconComponent size={22} />
                    </div>
                    <h3 className="font-display text-3xl font-black tracking-tight">
                      {s.name}
                    </h3>
                  </div>
                  <p className="mt-6 max-w-3xl text-white/75">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-16">
        <Reveal>
          <div className="eyebrow">{t.pillarsEyebrow}</div>
          <h2 className="font-display mt-6 max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl">
            {t.pillarsTitle}
          </h2>
        </Reveal>
        <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {t.pillarsList.map((p, idx) => {
            const IconComponent = p.icon;
            return (
              <StaggerItem
                key={idx}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 transition-colors hover:border-[#F68A31]/50"
              >
                <IconComponent className="text-[#F68A31]" size={24} />
                <h3 className="font-display mt-5 text-xl font-semibold">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-white/65">{p.desc}</p>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </section>

      {/* TIMELINE */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-16" data-testid="timeline-section">
        <Reveal>
          <div className="eyebrow">{t.timelineEyebrow}</div>
          <h2 className="font-display mt-6 max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl">
            {t.timelineTitle}
          </h2>
        </Reveal>
        <div className="relative mt-14">
          <div className="absolute left-3 top-2 bottom-2 w-px bg-white/15 md:left-1/2" />
          <StaggerGroup className="space-y-10">
            {t.timelineList.map((item, idx) => (
              <StaggerItem
                key={idx}
                className="relative pl-12 md:grid md:grid-cols-2 md:gap-12 md:pl-0"
              >
                <div className="md:px-12">
                  <div className="absolute left-0 top-1 h-6 w-6 rounded-full border-4 border-[#0A0A0A] bg-[#F68A31] md:left-[calc(50%-12px)]" />
                  <div className="font-display text-3xl font-black text-[#F68A31]">
                    {item.year}
                  </div>
                  <h3 className="font-display mt-2 text-2xl font-semibold">
                    {item.title}
                  </h3>
                  <p className="mt-2 max-w-md text-white/65">{item.desc}</p>
                </div>
                <div />
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
}